import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Plus, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import RowActions from "../components/RowActions";
import FileAttach, { AttachmentList } from "../components/FileAttach";
import { useAuth } from "../contexts/AuthContext";
import { can } from "../lib/rbac";
import { pickDefaultSchool } from "../lib/autoSelect";
import { localName, localFullName } from "../lib/localName";

const EMPTY = { subject_id: "", title: "", description: "", due_date: "", attachments: [], meeting_url: "", meeting_platform: "" };

export default function HomeworkPage() {
  const {  t , lang } = useI18n();
  const { user } = useAuth();
  const canEdit = can.addHomework(user);
  const isParent = user?.role === "parent";
  const isStudent = user?.role === "student";
  const isPersonal = isParent || isStudent;

  const [schools, setSchools] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [items, setItems] = useState([]);
  const [school, setSchool] = useState("");
  const [classId, setClassId] = useState("");

  // Personal (parent/student) state
  const [myChildren, setMyChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState("");

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [defaults, setDefaults] = useState({ meeting_url: "", meeting_platform: "" });

  // ---------- Loaders ----------
  useEffect(() => {
    if (!isPersonal) api.get("/schools").then(r => {
      const list = r.data || [];
      setSchools(list);
      setSchool(prev => prev || pickDefaultSchool(list, user));
    });
  }, [isPersonal]);

  // Personal: load children / own student record + their class subjects
  useEffect(() => {
    if (isParent) {
      api.get("/my/children").then(r => {
        const kids = r.data || [];
        setMyChildren(kids);
        if (kids.length && !selectedChild) {
          const k = kids[0];
          setSelectedChild(k.id);
          setClassId(k.class_id);
          // Iter45: filter subjects by the child's class
          if (k.school_id && k.class_id)
            api.get(`/subjects?school_id=${k.school_id}&class_id=${k.class_id}`).then(rr => setSubjects(rr.data || []));
        }
      });
    } else if (isStudent) {
      api.get("/my/student-record").then(r => {
        const s = r.data;
        if (s) {
          setMyChildren([s]); setSelectedChild(s.id); setClassId(s.class_id);
          if (s.school_id && s.class_id)
            api.get(`/subjects?school_id=${s.school_id}&class_id=${s.class_id}`).then(rr => setSubjects(rr.data || []));
        }
      });
    }
  }, [isParent, isStudent]);

  const pickChild = (cid) => {
    const c = myChildren.find(k => k.id === cid);
    if (!c) return;
    setSelectedChild(cid); setClassId(c.class_id);
    if (c.school_id && c.class_id)
      api.get(`/subjects?school_id=${c.school_id}&class_id=${c.class_id}`).then(r => setSubjects(r.data || []));
  };

  useEffect(() => {
    api.get("/settings/public").then(r => setDefaults({
      meeting_url: r.data?.default_meeting_url || "",
      meeting_platform: r.data?.default_meeting_platform || "",
    })).catch(() => {});
  }, []);

  // Staff: load classes + subjects of selected school/class
  useEffect(() => {
    if (isPersonal || !school) return;
    api.get(`/classes?school_id=${school}`).then(r => {
      const list = r.data || [];
      setClasses(list);
      setClassId(prev => (prev && list.some(c => c.id === prev)) ? prev : (list[0]?.id || ""));
    });
  }, [school, isPersonal]);
  useEffect(() => {
    if (isPersonal || !school) return;
    // Iter45: refine subjects by class when one is chosen
    const url = classId
      ? `/subjects?school_id=${school}&class_id=${classId}`
      : `/subjects?school_id=${school}`;
    api.get(url).then(r => setSubjects(r.data || []));
  }, [school, classId, isPersonal]);

  const load = () => { if (classId) api.get(`/homework?class_id=${classId}`).then(r => setItems(r.data || [])); };
  useEffect(() => { load(); }, [classId]);

  const openNew = () => {
    setEditing(null);
    setForm({ ...EMPTY, meeting_url: defaults.meeting_url, meeting_platform: defaults.meeting_platform });
    setOpen(true);
  };
  const openEdit = (it) => { setEditing(it); setForm({ ...EMPTY, ...it }); setOpen(true); };
  const submit = async () => {
    const payload = { ...form, class_id: classId };
    if (editing) await api.put(`/homework/${editing.id}`, payload);
    else await api.post("/homework", payload);
    toast.success(t("success")); setOpen(false); load();
  };
  const remove = async (it) => { await api.delete(`/homework/${it.id}`); toast.success(t("success")); load(); };

  return (
    <div className="space-y-6">
      <div className="flex justify-between flex-wrap gap-3 items-center">
        <h1 className="text-3xl font-semibold">{t("homework")}</h1>
        <div className="flex gap-3 flex-wrap">
          {isPersonal ? (
            isParent && myChildren.length > 1 ? (
              <Select value={selectedChild} onValueChange={pickChild}>
                <SelectTrigger className="w-56" data-testid="hw-child-select"><SelectValue placeholder={t("select_child")} /></SelectTrigger>
                <SelectContent>{myChildren.map(c => <SelectItem key={c.id} value={c.id}>{localFullName(c, lang)}</SelectItem>)}</SelectContent>
              </Select>
            ) : myChildren.length === 1 ? (
              <span className="text-sm text-muted-foreground bg-muted/40 px-3 py-2 rounded-md">
                {myChildren[0].full_name_ar || myChildren[0].full_name}
              </span>
            ) : (
              <span className="text-sm text-muted-foreground">{t("no_linked_children")}</span>
            )
          ) : (
            <>
              <Select value={school} onValueChange={setSchool}>
                <SelectTrigger className="w-48"><SelectValue placeholder={t("select_school")} /></SelectTrigger>
                <SelectContent>{schools.map(s => <SelectItem key={s.id} value={s.id}>{localName(s, lang)}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={classId} onValueChange={setClassId}>
                <SelectTrigger className="w-48" data-testid="hw-class"><SelectValue placeholder={t("select_class")} /></SelectTrigger>
                <SelectContent>{classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </>
          )}
          {canEdit && <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button onClick={openNew} disabled={!classId} data-testid="new-hw-btn"><Plus className="w-4 h-4 me-2" />{t("new_homework")}</Button></DialogTrigger>
            <DialogContent><DialogHeader><DialogTitle>{editing ? t("edit") : t("new_homework")}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>{t("select_subject")}</Label>
                  <Select value={form.subject_id} onValueChange={v => setForm({ ...form, subject_id: v })}><SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{subjects.map(s => <SelectItem key={s.id} value={s.id}>{localName(s, lang)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>{t("title")}</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} /></div>
                <div><Label>{t("description")}</Label><Textarea rows={4} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
                <div><Label>{t("due_date")}</Label><Input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} /></div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2"><Label>{t("meeting_url")}</Label><Input value={form.meeting_url} onChange={e => setForm({ ...form, meeting_url: e.target.value })} placeholder={defaults.meeting_url || "https://meet.google.com/..."} data-testid="hw-meeting-url" /></div>
                  <div><Label>{t("meeting_platform")}</Label>
                    <Select value={form.meeting_platform || ""} onValueChange={v => setForm({ ...form, meeting_platform: v })}><SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent><SelectItem value="zoom">Zoom</SelectItem><SelectItem value="teams">Teams</SelectItem><SelectItem value="meet">Meet</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent>
                    </Select>
                  </div>
                </div>
                {defaults.meeting_url && (
                  <button type="button" className="text-xs text-primary underline" onClick={() => setForm({ ...form, meeting_url: defaults.meeting_url, meeting_platform: defaults.meeting_platform })} data-testid="hw-use-default-meeting">
                    {t("use_default_meeting")}
                  </button>
                )}
                <div><Label>Attachments</Label><FileAttach value={form.attachments || []} onChange={(v) => setForm({ ...form, attachments: v })} testIdPrefix="hw-attach" /></div>
                <Button onClick={submit} className="w-full" data-testid="hw-save">{t("save")}</Button>
              </div>
            </DialogContent>
          </Dialog>}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {items.map(h => (
          <Card key={h.id} className="p-5 border shadow-none card-hover smooth" data-testid={`hw-${h.id}`}>
            <div className="flex gap-3">
              <div className="w-10 h-10 rounded-lg bg-accent/10 text-accent grid place-items-center"><FileSpreadsheet className="w-5 h-5" /></div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div className="font-semibold">{h.title}</div>
                  {canEdit && <RowActions item={h} onEdit={openEdit} onDelete={remove} testIdPrefix="hw" />}
                </div>
                <div className="text-xs text-muted-foreground">{subjects.find(s => s.id === h.subject_id)?.name_ar || subjects.find(s => s.id === h.subject_id)?.name || "—"} • {t("due_date")}: {h.due_date}</div>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{h.description}</p>
                {h.meeting_url && <a href={h.meeting_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 mt-2 text-xs text-primary underline" data-testid={`hw-join-${h.id}`}>🔗 Join {h.meeting_platform || "online"}</a>}
                <AttachmentList files={h.attachments || []} testIdPrefix={`hw-${h.id}-att`} />
              </div>
            </div>
          </Card>
        ))}
        {items.length === 0 && (
          <Card className="p-8 col-span-full text-center text-muted-foreground border-dashed">
            {isParent && !classId ? t("no_linked_children") : t("no_data")}
          </Card>
        )}
      </div>
    </div>
  );
}
